import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { Badge, EmptyState, Table, Tabs, type TableColumn } from "../../../components/ui";
import { SectionFrame } from "../components/SectionFrame";
import { OperationDetailModal } from "../components/OperationDetailModal";
import { useOperations } from "../hooks/useRadarCorreo";
import type { Operation } from "../services/radarCorreoService";
import { OP_STATUS_TONE, STAGE_TONE, fmtDate, laneLabel, primaryRef } from "../lib/format";

const FILTERS = ["proposed", "confirmed", "dismissed", "all"] as const;

export default function OperacionesSection() {
  const { t, i18n } = useTranslation("radarCorreo");
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>("proposed");
  const ops = useOperations(filter === "all" ? {} : { status: filter });
  const openId = params.get("id");

  const columns = useMemo<TableColumn<Operation>[]>(
    () => [
      {
        key: "ref",
        header: t("operations.columns.reference"),
        render: (o) => (
          <span className="font-medium text-content">
            {primaryRef(o) || t("operations.noReference")}
            {o.conflict && <i className="ri-error-warning-line ml-1 text-warning" title={t("operations.conflict")} />}
          </span>
        ),
      },
      { key: "cp", header: t("operations.columns.counterparty"), render: (o) => (o.counterparties || []).map((c) => c.name || c.id).join(", ") || "—" },
      { key: "lane", header: t("operations.columns.lane"), hideOnMobile: true, render: (o) => laneLabel(o) },
      { key: "stage", header: t("operations.columns.stage"), render: (o) => <Badge tone={STAGE_TONE[o.stage]}>{t(`operations.stage.${o.stage}`)}</Badge> },
      { key: "eta", header: t("operations.columns.eta"), hideOnMobile: true, render: (o) => fmtDate(o.etaAt, i18n.language) },
      { key: "last", header: t("operations.columns.last"), hideOnMobile: true, render: (o) => fmtDate(o.lastEventAt, i18n.language) },
      { key: "status", header: t("operations.columns.status"), render: (o) => <Badge tone={OP_STATUS_TONE[o.status]} variant="outline">{t(`operations.status.${o.status}`)}</Badge> },
    ],
    [t, i18n.language],
  );

  const rows = [...(ops.data || [])].sort((a, b) => (b.lastEventAt || 0) - (a.lastEventAt || 0));

  return (
    <SectionFrame
      title={t("operations.title")}
      subtitle={t("operations.subtitle")}
      loading={ops.isLoading}
      error={ops.error}
      onRetry={() => void ops.refetch()}
      filters={
        <Tabs
          items={FILTERS.map((f) => ({ id: f, label: t(`operations.filters.${f}`) }))}
          activeId={filter}
          onChange={(id) => setFilter(id as (typeof FILTERS)[number])}
        />
      }
    >
      {rows.length === 0 ? (
        <EmptyState icon="ri-ship-line" title={t("operations.empty")} />
      ) : (
        <Table columns={columns} rows={rows} rowKey={(o) => o.id} onRowClick={(o) => setParams({ id: o.id })} mobileCards />
      )}
      {openId && (
        <OperationDetailModal
          operationId={openId}
          onClose={() => {
            params.delete("id");
            setParams(params);
          }}
        />
      )}
    </SectionFrame>
  );
}
